import { useMutation, useQuery, useApolloClient } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { DOWNLOAD_STUDENT_RESULT } from '@/graphql';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { GET_STUDENTS } from '@/graphql';
import { useResultProcessing } from '@/composables/useResultProcessing';
import { useStudentResultQuery } from '@/composables/queries/useStudentResultQuery';
import { useReportCardUtils } from '@/composables/useReportCardUtils';
import { ResultStatus, EnrollmentStatusEnum } from '@/types/composition-functions';

import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';

import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import {
  useUpdateResultTemplateMutation,
  usePublishResultMutation,
  useSaveStudentResultFeedbackMutation,
} from '@/types/vue-apollo-helpers';
import { extractBulkPublishFailures, type BulkPublishFailure } from './bulkPublishFailures';
import { buildFeedbackSaveFields } from '@/utils/resultFeedbackRating';

import { extractGraphQLError } from '@/utils/graphqlError';
export function useViewResults() {
  const { isTertiary } = useSchoolFeatures();
  const { classrooms, academicCalendars, school } = useSchoolQuery();

  const selectedTerm = ref();
  const selectedClassroom = ref();
  const selectedStudent = ref();
  const selectedProgramme = ref();
  const selectedLevel = ref();
  const selectedStatus = ref<EnrollmentStatusEnum | null>(null);
  const confirm = useConfirm();
  const toast = useToast();
  const studentSearch = ref('');
  const page = ref(1);
  const rows = ref(10); //
  const totalRecords = ref(0);

  const studentFilter = computed(() => {
    const filter: Record<string, unknown> = {};

    // 🔹 Academic term is optional but allowed
    if (selectedTerm.value) {
      filter.academic_calendar_id = selectedTerm.value;
    }

    if (isTertiary.value) {
      if (selectedProgramme.value) {
        filter.programme_id = selectedProgramme.value;
      }

      if (selectedLevel.value) {
        filter.classroom_section_ids = [selectedLevel.value];
      }
    } else {
      if (selectedClassroom.value) {
        filter.classroom_section_ids = [selectedClassroom.value];
      }
    }

    // 🔥 Search works independently
    if (studentSearch.value?.length) {
      filter.search = studentSearch.value;
    }

    if (selectedStatus.value) {
      filter.status = selectedStatus.value;
    }

    return filter;
  });

  let searchTimeout: ReturnType<typeof setTimeout> | undefined;

  function onStudentFilter(event: { value?: string }) {
    const value = event.value?.trim() || '';

    clearTimeout(searchTimeout);

    searchTimeout = setTimeout(() => {
      studentSearch.value = value;
    }, 300);
  }

  const classListSearch = ref('');
  let classListSearchTimeout: ReturnType<typeof setTimeout> | undefined;

  function onClassListSearch() {
    clearTimeout(classListSearchTimeout);
    classListSearchTimeout = setTimeout(() => {
      studentSearch.value = classListSearch.value.trim();
      page.value = 1;
    }, 400);
  }

  const studentsQueryEnabled = computed(() => {
    if (isTertiary.value) {
      return !!selectedProgramme.value;
    }

    if (studentSearch.value?.length) return true;

    return !!selectedClassroom.value;
  });

  const isSearching = computed(() => !!studentSearch.value?.length);
  const {
    result: studentsResult,
    loading: loadingStudents,
    onError,
    refetch: refetchStudents,
  } = useQuery(
    GET_STUDENTS,
    () => ({
      page: isSearching.value ? 1 : page.value,
      first: isSearching.value ? 100 : rows.value,
      filter: studentFilter.value,
      include_tertiary: isTertiary.value,
    }),
    {
      enabled: studentsQueryEnabled,
      fetchPolicy: 'network-only',
    },
  );

  onError((error) => {
  });

  async function onFilterChange() {
    await refetchStudents();
  }

  const studentsList = computed(() => {
    const response = studentsResult.value?.students;

    if (!response) return [];

    totalRecords.value = response.paginatorInfo?.total ?? 0;

    return response.data.map((s: { id: string; surname: string; first_name: string; other_name?: string; matric_no: string; gender: string }) => ({
      id: s.id,
      name: `${s.surname} ${s.first_name} ${s.other_name || ''}`.trim(),
      matric_no: s.matric_no,
      gender: s.gender,
    }));
  });

  function onPageChange(event: { page: number; rows: number }) {
    page.value = event.page + 1; // PrimeVue is 0-based
    rows.value = event.rows;
  }

  const studentOptions = computed(() => studentsList.value.map((s: { name: string; id: string }) => ({
    label: s.name,
    value: s.id,
  })));

  watch(studentSearch, (v) => {
    if (v?.length) {
      selectedClassroom.value = null;
    }
  });

  watch(selectedClassroom, () => {
    studentSearch.value = '';
    classListSearch.value = '';
  });

  watch(selectedProgramme, () => {
    studentSearch.value = '';
    classListSearch.value = '';
  });

  watch(studentSearch, () => {
    selectedStudent.value = null;
  });

  watch(
    () => studentFilter.value,
    () => {
      page.value = 1;
    },
    { deep: true },
  );

  watch(studentSearch, async(val) => {
    if (!studentsQueryEnabled.value) return;

    page.value = 1;
    await refetchStudents();
  });

  const showStudentsTable = computed(() => {
    if (selectedStudent.value) return false;

    if (isTertiary.value) {
      return !!selectedProgramme.value;
    }

    if (studentSearch.value?.length) return true;

    return !!selectedClassroom.value;
  });

  // Fetch Student Result Logic
  const enabledQuery = computed(
    () => !!selectedStudent.value && !!selectedTerm.value && !isTertiary.value,
  );

  const {
    studentResult,
    attendanceResult,
    currentSessionData,
    loadingResult,
    loadingAttendance,
    refetchResult,
  } = useStudentResultQuery(selectedStudent, selectedTerm, enabledQuery);

  const localChartColumn = ref('');
  const localChartType = ref('radar');

  // Initialize localChartColumn when studentResult loads
  watch(
    () => studentResult.value?.template,
    (template) => {
      if (template?.properties?.chart?.column) {
        localChartColumn.value = template.properties.chart.column;
      } else {
        localChartColumn.value = '';
      }

      if (template?.properties?.chart?.type) {
        localChartType.value = template.properties.chart.type;
      } else {
        localChartType.value = 'radar';
      }
    },
    { immediate: true, deep: true },
  );

  const {
    studentName,
    studentClass,
    leftColumnInfo,
    rightColumnInfo,
    teacherComment,
    principalComment,
    feedbacks,
    categorizedFeedbacks,
    nextTermDate,
    styleCode,
    chartColumn,
  } = useReportCardUtils(
    studentResult,
    attendanceResult,
    currentSessionData,
    academicCalendars,
    selectedTerm,
  );

  const { mutate: updateTemplate, loading: updatingTemplate } = useUpdateResultTemplateMutation();

  const saveChartDefaults = async(column: string, type: string) => {
    const template = studentResult.value?.template;
    if (!template?.id) return false;

    try {
      const properties: Record<string, unknown> = template.properties ? { ...template.properties } : {};
      properties.chart = {
        ...(properties.chart || {}),
        type: type,
        column: column,
      };

      const response = await updateTemplate({
        input: {
          id: template.id,
          properties: Object.keys(properties).length > 0 ? properties : null,
        },
      });

      if (response?.data?.updateResultTemplate) {
        toast.add({ severity: 'success', summary: 'Success', detail: 'Chart defaults saved successfully', life: 3000 });
        return true;
      }
      return false;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Failed to save chart defaults'), life: 3000 });
      return false;
    }
  };

  const {
    grades,
    calculateGrade,
    tableHeaders,
    processedSubjects: subjects,
    chartData,
  } = useResultProcessing(studentResult, localChartColumn);

  const UNPUBLISH_RESULT = gql`
    mutation UnPublishStudentResult(
      $academic_calendar_id: ID!
      $student_id: ID!
    ) {
      unPublishStudentResult(
        academic_calendar_id: $academic_calendar_id
        student_id: $student_id
      ) {
        id
        status
      }
    }
  `;

  const BULK_PUBLISH_RESULTS = gql`
    mutation BulkPublishResults(
      $academic_calendar_id: ID!
      $classroom_section_id: ID!
    ) {
      bulkPublishResults(
        academic_calendar_id: $academic_calendar_id
        classroom_section_id: $classroom_section_id
      ) {
        success
        message
        code
        failures {
          student_id
          student_name
          reason
        }
      }
    }
  `;

  const BULK_UNPUBLISH_RESULTS = gql`
    mutation BulkUnPublishResults(
      $academic_calendar_id: ID!
      $classroom_section_id: ID!
    ) {
      bulkUnPublishResults(
        academic_calendar_id: $academic_calendar_id
        classroom_section_id: $classroom_section_id
      ) {
        success
        message
        code
      }
    }
  `;

  const { mutate: publishMutation, loading: publishing } =
    usePublishResultMutation();
  const { mutate: unPublishMutation, loading: unPublishing } =
    useMutation(UNPUBLISH_RESULT);
  const { mutate: saveFeedbackMutation, loading: savingFeedback } =
    useSaveStudentResultFeedbackMutation();
  const { mutate: bulkPublishMutation, loading: bulkPublishing } =
    useMutation(BULK_PUBLISH_RESULTS);
  // Per-student failures from the most recent bulk publish, surfaced to the admin so they
  // can see exactly which students were skipped and why (partial-success is intentional).
  const bulkPublishFailures = ref<BulkPublishFailure[]>([]);
  const { mutate: bulkUnPublishMutation, loading: bulkUnPublishing } =
    useMutation(BULK_UNPUBLISH_RESULTS);

  async function publishResult() {
    if (!selectedTerm.value || !selectedStudent.value) return;

    try {
      await publishMutation({
        calendarId: selectedTerm.value,
        studentId: selectedStudent.value,
      });
      await refetchResult();
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Result published successfully!',
        life: 3000,
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'An unexpected error occurred';
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to publish result: ' + message,
        life: 3000,
      });
    }
  }

  async function unPublishResult() {
    if (!selectedTerm.value || !selectedStudent.value) return;

    confirm.require({
      message: 'Are you sure you want to unpublish this result?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Cancel',
      acceptLabel: 'Unpublish',
      rejectClass: 'p-button-secondary p-button-outlined',
      acceptClass: 'p-button-danger',
      accept: async() => {
        try {
          await unPublishMutation({
            academic_calendar_id: selectedTerm.value,
            student_id: selectedStudent.value,
          });
          await refetchResult();
          toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Result unpublished successfully!',
            life: 3000,
          });
        } catch (e: unknown) {
          const message = e instanceof Error ? e.message : 'An unexpected error occurred';
          toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to unpublish result: ' + message,
            life: 3000,
          });
        }
      },
      reject: () => {
        // callback to execute when user rejects the action
      },
    });
  }

  const bulkSectionId = computed(() => {
    if (isTertiary.value) return selectedLevel.value;
    return selectedClassroom.value;
  });

  async function bulkPublishResults() {
    if (!selectedTerm.value || !bulkSectionId.value) return;

    confirm.require({
      message: 'This will publish results for ALL students in this class for the selected term. Continue?',
      header: 'Bulk Publish Results',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Cancel',
      acceptLabel: 'Publish All',
      rejectClass: 'p-button-secondary p-button-outlined',
      acceptClass: 'p-button-primary',
      accept: async() => {
        bulkPublishFailures.value = [];
        try {
          const result = await bulkPublishMutation({
            academic_calendar_id: selectedTerm.value,
            classroom_section_id: bulkSectionId.value,
          });
          const response = result?.data?.bulkPublishResults;
          bulkPublishFailures.value = extractBulkPublishFailures(response);
          toast.add({
            severity: response?.success ? 'success' : 'warn',
            summary: response?.success ? 'Published' : 'Partial',
            detail: response?.message || 'Bulk publish completed.',
            life: 5000,
          });
          await refetchStudents();
        } catch (e: unknown) {
          const message = e instanceof Error ? e.message : 'An unexpected error occurred';
          toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Bulk publish failed: ' + message,
            life: 5000,
          });
        }
      },
    });
  }

  async function bulkUnPublishResults() {
    if (!selectedTerm.value || !bulkSectionId.value) return;

    confirm.require({
      message: 'This will unpublish results for ALL students in this class for the selected term. Continue?',
      header: 'Bulk Unpublish Results',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Cancel',
      acceptLabel: 'Unpublish All',
      rejectClass: 'p-button-secondary p-button-outlined',
      acceptClass: 'p-button-danger',
      accept: async() => {
        bulkPublishFailures.value = [];
        try {
          const result = await bulkUnPublishMutation({
            academic_calendar_id: selectedTerm.value,
            classroom_section_id: bulkSectionId.value,
          });
          const response = result?.data?.bulkUnPublishResults;
          toast.add({
            severity: 'success',
            summary: 'Unpublished',
            detail: response?.message || 'Bulk unpublish completed.',
            life: 5000,
          });
          await refetchStudents();
        } catch (e: unknown) {
          const message = e instanceof Error ? e.message : 'An unexpected error occurred';
          toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Bulk unpublish failed: ' + message,
            life: 5000,
          });
        }
      },
    });
  }

  async function saveFeedback(
    feedbackId: string | undefined,
    resultTemplateFeedbackId: string,
    comment: string,
    rating?: number | null,
  ) {
    if (!selectedStudent.value || !selectedTerm.value) return;

    const definition = feedbacks.value.find((f: { id: string }) => f.id === resultTemplateFeedbackId) ?? {};

    try {
      await saveFeedbackMutation({
        input: {
          student_id: selectedStudent.value,
          academic_calendar_id: selectedTerm.value,
          result_template_feedback_id: resultTemplateFeedbackId,
          ...buildFeedbackSaveFields(definition, comment, rating),
        },
      });
      toast.add({
        severity: 'success',
        summary: 'Saved',
        detail: 'Feedback saved successfully',
        life: 2000,
      });
      // Optionally refetch to update IDs or rely on cache if Apollo handles it well
      refetchResult(); // Optimistic UI or refetch
    } catch (e: unknown) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: extractGraphQLError(e, 'Failed to save feedback'),
        life: 3000,
      });
    }
  }

  function onTermSelect(value: string) {
    selectedTerm.value = value;
  }
  function onStudentSelect(value: string) {
    selectedStudent.value = value;
  }

  // Result Summary (percentage, average, etc.)
  const resultSummary = computed(() => studentResult.value?.summary);

  const chartColumnOptions = computed(() => {
    if (!studentResult.value?.headers) return [];
    return studentResult.value.headers
      .filter((h: { is_visible: boolean; label: string; key: string }) => h.is_visible && h.label !== 'Subject')
      .map((h: { is_visible: boolean; label: string; key: string }) => ({
        label: h.label,
        value: h.key,
      }));
  });

  const { resolveClient } = useApolloClient();
  const downloading = ref(false);

  async function downloadPdf() {
    if (!selectedStudent.value || !selectedTerm.value) return;
    downloading.value = true;
    try {
      const client = resolveClient();
      const { data } = await client.query({
        query: DOWNLOAD_STUDENT_RESULT,
        variables: {
          student_id: selectedStudent.value,
          academic_calendar_id: selectedTerm.value,
        },
        fetchPolicy: 'no-cache',
      });
      if (!data?.downloadStudentResult?.id) {
        toast.add({ severity: 'warn', summary: 'Could not start export', detail: 'Please try again.', life: 4000 });
        return;
      }
      toast.add({
        severity: 'success',
        summary: 'Export Started',
        detail: 'Your result PDF is being generated. Check the Downloads icon in the top bar for progress.',
        life: 5000,
      });
    } catch (e: unknown) {
      toast.add({ severity: 'error', summary: 'Download Failed', detail: 'Could not start the result PDF export.', life: 4000 });
    } finally {
      downloading.value = false;
    }
  }

  return {
    school,
    classrooms,
    selectedTerm,
    selectedClassroom,
    selectedStudent,
    loadingStudents,
    studentsList,
    studentOptions,
    studentResult,
    ResultStatus,
    grades,
    calculateGrade,
    tableHeaders,
    subjects,
    studentName,
    studentClass,
    leftColumnInfo,
    rightColumnInfo,
    teacherComment,
    principalComment,
    feedbacks,
    nextTermDate,
    publishing,
    unPublishing,
    publishResult,
    unPublishResult,
    saveFeedback,
    onTermSelect,
    onStudentSelect,
    loadingResult,
    resultSummary,
    selectedProgramme,
    selectedLevel,
    selectedStatus,
    onFilterChange,
    onStudentFilter,
    classListSearch,
    onClassListSearch,
    page,
    rows,
    totalRecords,
    onPageChange,
    showStudentsTable,
    categorizedFeedbacks,
    styleCode,
    chartData,
    localChartColumn,
    localChartType,
    chartColumnOptions,
    saveChartDefaults,
    updatingTemplate,
    downloadPdf,
    downloading,
    bulkPublishResults,
    bulkUnPublishResults,
    bulkPublishing,
    bulkUnPublishing,
    bulkPublishFailures,
  };
}
